import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success(r.data.message || "If an account exists, you'll receive an email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="py-20 min-h-[70vh] bg-[var(--ft-soft-gray)]">
        <div className="ft-container max-w-md">
          <Link to="/login" className="text-sm text-[var(--ft-muted)] hover:text-[var(--ft-black)] inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
          <div className="ft-card">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={18} className="text-[var(--ft-gold)]" />
              <div className="ft-eyebrow">Account Security</div>
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
            <p className="text-sm text-[var(--ft-muted)] mt-2">
              Enter the email tied to your FLOWTALIS™ account and we&apos;ll send a secure reset link.
            </p>
            {sent ? (
              <div className="mt-6 p-4 rounded-lg bg-[var(--ft-gold-soft)] border border-[var(--ft-gold)]" data-testid="forgot-sent">
                <ShieldCheck size={18} className="text-[var(--ft-gold)] mb-2" />
                <p className="text-sm">
                  If an account exists for that email, you&apos;ll receive a reset link within a minute.
                  The link expires in 60 minutes for your security.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="ft-label block mb-1.5">Email address</label>
                  <input
                    type="email"
                    required
                    className="ft-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                  />
                </div>
                <button type="submit" disabled={loading} className="ft-btn-primary w-full" data-testid="forgot-submit">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                  Send Reset Link
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset. Please sign in.");
      setTimeout(() => navigate("/login"), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="py-20 min-h-[70vh] bg-[var(--ft-soft-gray)]">
        <div className="ft-container max-w-md">
          <div className="ft-card">
            <div className="ft-eyebrow mb-2">Account Security</div>
            <h1 className="text-2xl font-bold tracking-tight">Choose a new password</h1>
            {done ? (
              <div className="mt-6 p-4 rounded-lg bg-[var(--ft-gold-soft)] border border-[var(--ft-gold)]" data-testid="reset-done">
                <ShieldCheck size={18} className="text-[var(--ft-gold)] mb-2" />
                <p className="text-sm">Password updated. Redirecting you to sign in…</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="ft-label block mb-1.5">New password</label>
                  <input type="password" required className="ft-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" />
                </div>
                <div>
                  <label className="ft-label block mb-1.5">Confirm password</label>
                  <input type="password" required className="ft-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
                </div>
                <button type="submit" disabled={loading || !token} className="ft-btn-primary w-full" data-testid="reset-submit">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                  Update Password
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};
